/**
 * feat-307-redesign-tenant-mgmt (M9 / B6) — "New tenant" as a modal.
 *
 * This is develop's full-width "Create New Tenant" panel moved into a dialog:
 * the SAME two fields (Tenant Name *, Description), the SAME ids, labels and
 * placeholders, and the SAME submit handler (`handleCreateTenant` in
 * TenantDashboard, `POST /api/tenant`). Only the container changed.
 */
import React, { useEffect } from 'react';
import { X } from './icons';

export interface NewTenantModalProps {
  values: { tenant_name: string; description: string };
  onChange: (next: { tenant_name: string; description: string }) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export default function NewTenantModal({ values, onChange, onSubmit, onClose, isSubmitting, error }: NewTenantModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-tenant-title"
        data-testid="new-tenant-modal"
        className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 id="new-tenant-title" className="font-display text-xl font-semibold text-ink">
            Create New Tenant
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-inkMuted hover:text-ink">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
        {error && (
          <p role="alert" className="mb-4 rounded-lg border border-danger-line/40 bg-danger-surface px-3 py-2 text-sm text-danger-text">
            {error}
          </p>
        )}
        <form onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div>
              <label className="block text-ink text-sm font-semibold mb-2" htmlFor="tenant_name">
                Tenant Name <span className="text-danger-text">*</span>
              </label>
              <input
                id="tenant_name"
                name="tenant_name"
                type="text"
                value={values.tenant_name}
                onChange={(e) => onChange({ ...values, tenant_name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-softBorder bg-white text-sm focus:outline-none focus:ring-2 focus:ring-coral/40"
                placeholder="Enter tenant name"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-ink text-sm font-semibold mb-2" htmlFor="description">
                Description
              </label>
              <input
                id="description"
                name="description"
                type="text"
                value={values.description}
                onChange={(e) => onChange({ ...values, description: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-softBorder bg-white text-sm focus:outline-none focus:ring-2 focus:ring-coral/40"
                placeholder="Enter description (optional)"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-lg bg-white border border-softBorder text-ink px-4 py-2 text-sm font-bold hover:bg-cream-dark"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex items-center rounded-lg bg-coral text-white px-4 py-2 text-sm font-bold hover:brightness-95 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
